import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, User, Settings, ChevronDown, Menu, X, BarChart2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { classNames, cn } from '../../theme/utils';
import MobileMenu from './MobileMenu';
import ThemeToggle from '../Theme/ThemeToggle';

const MainNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const userMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);

  // Helper to get user info
  const getUserDisplayName = () => {
    return user?.user_metadata?.full_name || user?.user_metadata?.username || user?.email || 'User';
  };
  const getUserEmail = () => {
    if (!user?.email) return null;
    if (user.email.endsWith('@local.app')) return null;
    return user.email;
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setShowUserMenu(false);
  };

  const handleAccountClick = () => {
    navigate('/account');
    setShowUserMenu(false);
  };

  const closeMobileMenu = () => {
    setShowMobileMenu(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* top-0 while the "Launch Year" banner (App.js) is hidden — it was
          top-9 to sit below that 36px banner; revert together. */}
      <nav className="fixed inset-x-0 top-0 w-full bg-slate-950/95 border-b border-slate-800 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 min-w-0">
              {/* Desktop Logo */}
              <div className="hidden sm:flex items-center gap-2">
                <img
                  src="/favicon.png"
                  alt="FOOTY DRAFT HUB"
                  className="h-8 w-auto"
                />
                <div className="flex flex-col">
                  <span className="text-emerald-400 font-bold text-sm leading-tight tracking-wide">FOOTY</span>
                  <span className="text-white font-medium text-[11px] leading-tight tracking-widest">DRAFT HUB</span>
                </div>
              </div>
              {/* Mobile Logo */}
              <div className="flex sm:hidden items-center gap-2">
                <img
                  src="/favicon.png"
                  alt="FOOTY DRAFT HUB"
                  className="h-7 w-auto"
                />
                <div className="flex flex-col">
                  <span className="text-emerald-400 font-bold text-xs leading-tight tracking-wide">FOOTY</span>
                  <span className="text-white font-medium text-[10px] leading-tight tracking-widest">DRAFT HUB</span>
                </div>
              </div>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {user && (
                <Link
                  to="/leagues"
                  className={cn(
                    'text-sm font-medium transition-colors',
                    isActive('/leagues')
                      ? 'text-emerald-400'
                      : 'text-emerald-200/80 hover:text-white'
                  )}
                >
                  Leagues
                </Link>
              )}
              {/* Try Demo hidden for the time being — route/page still
                  exist, just uncomment this block to restore the nav entry.
              {!user && (
                <Link
                  to="/demo"
                  className={cn(
                    'text-sm font-medium transition-colors',
                    isActive('/demo')
                      ? 'text-emerald-400'
                      : 'text-emerald-200/80 hover:text-white'
                  )}
                >
                  Try Demo
                </Link>
              )}
              */}
              {/* Blog tab hidden for the time being — route/page still
                  exist, just uncomment this Link to restore the nav entry.
              <Link
                to="/blog"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive('/blog')
                    ? 'text-emerald-400'
                    : 'text-emerald-200/80 hover:text-white'
                )}
              >
                Blog
              </Link>
              */}
              <Link
                to="/rules"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive('/rules')
                    ? 'text-emerald-400'
                    : 'text-emerald-200/80 hover:text-white'
                )}
              >
                Rules
              </Link>
              <Link
                to="/fixtures"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive('/fixtures')
                    ? 'text-emerald-400'
                    : 'text-emerald-200/80 hover:text-white'
                )}
              >
                Fixtures
              </Link>
              <Link
                to="/stats"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive('/stats')
                    ? 'text-emerald-400'
                    : 'text-emerald-200/80 hover:text-white'
                )}
              >
                Stats
              </Link>
              {/* Player Tiers standalone page hidden from nav for now — route still works if linked directly. */}
              <div className="relative" ref={toolsMenuRef}>
                <button
                  onClick={() => setShowToolsMenu(!showToolsMenu)}
                  className={cn(
                    'flex items-center space-x-1 text-sm font-medium transition-colors',
                    ['/fdr', '/projections', '/lineups'].some(p => isActive(p))
                      ? 'text-emerald-400'
                      : 'text-emerald-200/80 hover:text-white'
                  )}
                >
                  <BarChart2 className="w-4 h-4 mr-1" />
                  Tools
                  <ChevronDown className={cn('w-4 h-4 transition-transform', showToolsMenu && 'rotate-180')} />
                </button>
                {showToolsMenu && (
                  <div className="absolute left-0 mt-2 w-52 theme-bg-surface border border-emerald-500/30 rounded-xl shadow-2xl py-1 z-50 animate-scale-in">
                    {[
                      { to: '/fdr', label: 'FDR Calendar' },
                      { to: '/projections', label: 'Player Projections' },
                      { to: '/lineups', label: 'Predicted Lineups' },
                      { to: '/player-news', label: 'Player News' },
                    ].map(({ to, label }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setShowToolsMenu(false)}
                        className={cn(
                          'block px-4 py-2 text-sm theme-transition',
                          isActive(to)
                            ? 'text-emerald-400 theme-bg-surface-hover'
                            : 'theme-text-secondary hover:theme-bg-surface-hover hover:theme-text-primary'
                        )}
                      >
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              {/* <Link
                to="/pricing"
                className={cn(
                  'text-sm font-medium transition-colors',
                  isActive('/pricing')
                    ? 'text-emerald-400'
                    : 'text-emerald-200/80 hover:text-white'
                )}
              >
                Pricing
              </Link> */} {/* Temporarily hidden */}
              
              {/* Theme Toggle - Hidden for now */}
              {/* <ThemeToggle size="sm" /> */}
              
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-emerald-800/30 transition-colors"
                  >
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <ChevronDown className={cn(
                      'w-4 h-4 text-emerald-200/80 transition-transform',
                      showUserMenu && 'rotate-180'
                    )} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 theme-bg-surface border border-emerald-500/30 rounded-xl shadow-2xl py-1 z-50 animate-scale-in">
                      {/* User Info */}
                      <div className="px-4 py-3 theme-border-b">
                        <div className="font-semibold theme-text-primary truncate">{getUserDisplayName()}</div>
                        {getUserEmail() && (
                          <div className="text-xs theme-text-muted truncate">{getUserEmail()}</div>
                        )}
                      </div>
                      {/* Actions */}
                      <button
                        onClick={handleAccountClick}
                        className="flex items-center w-full px-4 py-2 text-sm theme-text-secondary hover:theme-bg-surface-hover hover:theme-text-primary theme-transition"
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Account
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm theme-text-secondary hover:theme-bg-surface-hover hover:theme-text-primary theme-transition"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/auth"
                  className="px-4 py-2 text-sm font-medium rounded-md border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white bg-transparent transition-colors duration-150"
                >
                  Sign In
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 rounded-lg hover:bg-emerald-800/30 transition-colors"
                aria-label={showMobileMenu ? "Close menu" : "Open menu"}
              >
                {showMobileMenu ? (
                  <X className="w-6 h-6 text-emerald-200/80" />
                ) : (
                  <Menu className="w-6 h-6 text-emerald-200/80" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu 
        isOpen={showMobileMenu}
        onClose={closeMobileMenu}
        user={user}
        onSignOut={handleSignOut}
      />
    </>
  );
};

export default MainNav; 